import * as React from "react";
import { Layers } from "lucide-react";
import { Button } from "../ui/button";
import { SocialSvg } from "../SocialSvg";
import type { ReviewSourceSystem } from "@/services/bedrock";
import { cn } from "@/lib/utils";

/** Synthetic filter value for imported / non-network sources (BE, IF, EX). */
export const OTHER_SOURCE_FILTER = "OTHER" as const;
export type ReviewSourceFilterValue = ReviewSourceSystem | typeof OTHER_SOURCE_FILTER;

export const OTHER_SOURCE_SYSTEMS: ReviewSourceSystem[] = ["BE", "IF", "EX"];

export function resolveSourceSystemQueryParam(
  value: ReviewSourceFilterValue | null | undefined
): ReviewSourceSystem | ReviewSourceSystem[] | undefined {
  if (!value) return undefined;
  if (value === OTHER_SOURCE_FILTER) return OTHER_SOURCE_SYSTEMS;
  return value;
}

export const SOURCE_FILTER_LABELS: Record<ReviewSourceSystem, string> = {
  GL: "Google",
  FB: "Facebook",
  ZL: "Zillow",
  SC: "SocialCoach",
  BE: "Birdeye",
  IF: "Internal",
  EX: "Experience",
};

export function sourceFilterDisplayName(value: ReviewSourceFilterValue | null | undefined): string | null {
  if (!value) return null;
  if (value === OTHER_SOURCE_FILTER) return "Other";
  return SOURCE_FILTER_LABELS[value];
}

const SOURCES: Array<{ id: ReviewSourceFilterValue; title: string }> = [
  { id: "GL", title: SOURCE_FILTER_LABELS.GL },
  { id: "FB", title: SOURCE_FILTER_LABELS.FB },
  { id: "ZL", title: SOURCE_FILTER_LABELS.ZL },
  { id: "SC", title: SOURCE_FILTER_LABELS.SC },
  { id: OTHER_SOURCE_FILTER, title: "Other" },
];

const ROW_ACTIVE_CLASS = "bg-slate-100 border-slate-400 text-slate-900";
const ROW_IDLE_CLASS = "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50";

type ReviewSourceFilterButtonsProps = {
  value: ReviewSourceFilterValue | null;
  onChange: (value: ReviewSourceFilterValue | null) => void;
  /** `icons` = circular icon buttons (popover). `expandable` = labeled row (table column). */
  variant?: "icons" | "expandable";
};

function SourceIcon({
  id,
  selected,
  size = "xs",
  invertOnSelect = false,
}: {
  id: ReviewSourceFilterValue;
  selected?: boolean;
  size?: "xs" | "sm";
  /** When true, FB/ZL invert to white on selected (for dark/primary pill backgrounds). */
  invertOnSelect?: boolean;
}) {
  if (id === OTHER_SOURCE_FILTER) {
    const sizeClass = size === "sm" ? "size-5" : "size-4";
    return <Layers className={cn(sizeClass, selected ? "text-slate-900" : "text-slate-500")} aria-hidden />;
  }

  return (
    <SocialSvg
      networkId={id}
      size={size}
      bgColorClass="bg-transparent"
      iconColorClass={invertOnSelect && selected && (id === "FB" || id === "ZL") ? "text-white" : undefined}
    />
  );
}

export function ReviewSourceFilterButtons({ value, onChange, variant = "icons" }: ReviewSourceFilterButtonsProps) {
  if (variant === "expandable") {
    return <ExpandableSourceFilterButtons value={value} onChange={onChange} />;
  }

  return (
    <div className="flex flex-row flex-wrap gap-1.5">
      {SOURCES.map(source => {
        const selected = value === source.id;
        return (
          <Button
            key={source.id}
            type="button"
            variant={selected ? "default" : "outline"}
            size="icon"
            className="rounded-full transition-all flex items-center justify-center"
            onClick={() => onChange(selected ? null : source.id)}
            title={source.title}
          >
            <SourceIcon id={source.id} selected={selected} invertOnSelect />
          </Button>
        );
      })}
    </div>
  );
}

function ExpandableSourceFilterButtons({
  value,
  onChange,
}: {
  value: ReviewSourceFilterValue | null;
  onChange: (value: ReviewSourceFilterValue | null) => void;
}) {
  const [hoveredId, setHoveredId] = React.useState<ReviewSourceFilterValue | null>(null);
  const [focusedId, setFocusedId] = React.useState<ReviewSourceFilterValue | null>(null);
  const activeId = hoveredId ?? focusedId;

  return (
    <div className="flex h-14 w-full gap-1.5" role="group" aria-label="Source" onMouseLeave={() => setHoveredId(null)}>
      {SOURCES.map(({ id, title }) => {
        const selected = value === id;
        const showLabel = activeId === id || (selected && activeId == null);

        return (
          <button
            key={id}
            type="button"
            aria-pressed={selected}
            aria-label={title}
            title={title}
            className={cn(
              "flex h-full min-w-0 flex-col items-center justify-center overflow-hidden rounded-lg border px-1.5 transition-[flex-grow,flex-basis,colors] duration-300 ease-out cursor-pointer",
              showLabel ? "flex-[5] basis-0" : "flex-1 basis-0",
              selected ? ROW_ACTIVE_CLASS : ROW_IDLE_CLASS
            )}
            onMouseEnter={() => setHoveredId(id)}
            onFocus={() => setFocusedId(id)}
            onBlur={() => setFocusedId(current => (current === id ? null : current))}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onChange(selected ? null : id);
            }}
          >
            <span className="scale-125">
              <SourceIcon id={id} selected={selected} />
            </span>
            <span
              className={cn(
                "grid w-full transition-[grid-template-rows,opacity] duration-300 ease-out",
                showLabel ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0 mt-0"
              )}
              aria-hidden={!showLabel}
            >
              <span className="min-h-0 overflow-hidden">
                <span className="block truncate text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none text-center">
                  {title}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
